// src/admin/pages/StudentResults.jsx
import React, { useState, useMemo } from "react";
import {
  CheckSquare,
  PenTool,
  Search,
  Eye,
  CheckCircle2,
  XCircle,
  Download,
  Check,
  RotateCcw,
  Clock,
  GraduationCap,
} from "lucide-react";
import { useAdminData } from "../context/AdminDataContext";
import Modal from "../components/Modal";

const StudentResults = () => {
  const { studentResults, evaluateMainsSubmission, updateStudentResult } = useAdminData();

  // Active top tab: 'prelims' | 'mains'
  const [activeTab, setActiveTab] = useState("prelims");

  // Filter states for Prelims
  const [prelimsSearch, setPrelimsSearch] = useState("");
  const [prelimsTestFilter, setPrelimsTestFilter] = useState("All");
  const [prelimsSubjectFilter, setPrelimsSubjectFilter] = useState("All");
  const [prelimsDateFilter, setPrelimsDateFilter] = useState("");

  // Filter states for Mains
  const [mainsSearch, setMainsSearch] = useState("");
  const [mainsPaperFilter, setMainsPaperFilter] = useState("All");
  const [mainsInitiativeFilter, setMainsInitiativeFilter] = useState("All");
  const [mainsStatusFilter, setMainsStatusFilter] = useState("All");
  const [mainsDateFilter, setMainsDateFilter] = useState("");

  // Modals
  const [viewingPrelimsResult, setViewingPrelimsResult] = useState(null);
  const [evaluatingMainsResult, setEvaluatingMainsResult] = useState(null);

  // Mains Evaluation Form state
  const [mainsEvalForm, setMainsEvalForm] = useState({
    marksObtained: "",
    feedback: "",
    evaluationStatus: "Evaluated",
    evaluatorName: "Selva Kumar (Head Evaluator)",
  });

  // --------------------------------------------------------------------------
  // Filtered Prelims Data
  // --------------------------------------------------------------------------
  const allPrelimsResults = useMemo(() => {
    return (studentResults || []).filter((r) => r.examType === "Prelims");
  }, [studentResults]);

  const uniquePrelimsTests = useMemo(() => {
    const set = new Set();
    allPrelimsResults.forEach((r) => {
      if (r.initiativeName) set.add(r.initiativeName);
    });
    return Array.from(set);
  }, [allPrelimsResults]);

  const filteredPrelimsResults = useMemo(() => {
    return allPrelimsResults.filter((r) => {
      const matchesSearch =
        !prelimsSearch.trim() ||
        (r?.studentName || "").toLowerCase().includes(prelimsSearch.toLowerCase()) ||
        (r?.email || "").toLowerCase().includes(prelimsSearch.toLowerCase()) ||
        (r?.regId || "").toLowerCase().includes(prelimsSearch.toLowerCase());

      const matchesTest =
        prelimsTestFilter === "All" || r?.initiativeName === prelimsTestFilter;

      const matchesSubject =
        prelimsSubjectFilter === "All" ||
        (r?.initiativeName || "").toLowerCase().includes(prelimsSubjectFilter.toLowerCase());

      const matchesDate =
        !prelimsDateFilter ||
        (r?.submissionDate && r.submissionDate.startsWith(prelimsDateFilter));

      return matchesSearch && matchesTest && matchesSubject && matchesDate;
    });
  }, [
    allPrelimsResults,
    prelimsSearch,
    prelimsTestFilter,
    prelimsSubjectFilter,
    prelimsDateFilter,
  ]);

  // --------------------------------------------------------------------------
  // Filtered Mains Data
  // --------------------------------------------------------------------------
  const allMainsResults = useMemo(() => {
    return (studentResults || []).filter((r) => r.examType === "Mains");
  }, [studentResults]);

  const uniqueMainsInitiatives = useMemo(() => {
    const set = new Set();
    allMainsResults.forEach((r) => {
      if (r.initiativeName) set.add(r.initiativeName);
    });
    return Array.from(set);
  }, [allMainsResults]);

  const filteredMainsResults = useMemo(() => {
    return allMainsResults.filter((r) => {
      const matchesSearch =
        !mainsSearch.trim() ||
        (r?.studentName || "").toLowerCase().includes(mainsSearch.toLowerCase()) ||
        (r?.email || "").toLowerCase().includes(mainsSearch.toLowerCase()) ||
        (r?.regId || "").toLowerCase().includes(mainsSearch.toLowerCase());

      const matchesPaper =
        mainsPaperFilter === "All" ||
        (r?.gsPaper || "").toLowerCase().includes(mainsPaperFilter.toLowerCase());

      const matchesInitiative =
        mainsInitiativeFilter === "All" || r?.initiativeName === mainsInitiativeFilter;

      const currentStatus = r?.resultStatus || r?.status || "Pending";
      const matchesStatus =
        mainsStatusFilter === "All" ||
        (mainsStatusFilter === "Evaluated"
          ? currentStatus.toLowerCase().includes("evaluated")
          : !currentStatus.toLowerCase().includes("evaluated"));

      const matchesDate =
        !mainsDateFilter ||
        (r?.submissionDate && r.submissionDate.startsWith(mainsDateFilter));

      return matchesSearch && matchesPaper && matchesInitiative && matchesStatus && matchesDate;
    });
  }, [
    allMainsResults,
    mainsSearch,
    mainsPaperFilter,
    mainsInitiativeFilter,
    mainsStatusFilter,
    mainsDateFilter,
  ]);

  // --------------------------------------------------------------------------
  // Evaluation Handlers for Mains
  // --------------------------------------------------------------------------
  const openMainsEvalModal = (item) => {
    setEvaluatingMainsResult(item);
    const marks =
      item.marksObtained !== null && item.marksObtained !== undefined
        ? item.marksObtained
        : item.evaluatorMarks || Math.round((item.totalMarks || 15) * 0.7);

    setMainsEvalForm({
      marksObtained: marks,
      feedback:
        item.feedback ||
        item.evaluatorFeedback ||
        "Structure is sound. Introduction introduces key constitutional provisions accurately. Consider strengthening the conclusion with actionable policy recommendations and recent Law Commission / committee citations.",
      evaluationStatus: item.resultStatus === "Evaluated" || item.status === "Evaluated" ? "Evaluated" : "Evaluated",
      evaluatorName: item.evaluatorName || "Selva Kumar (Head Evaluator)",
    });
  };

  const handleSaveMainsEvaluation = (e) => {
    e.preventDefault();
    if (!evaluatingMainsResult) return;

    const marksNum = Number(mainsEvalForm.marksObtained);
    const maxMarks = evaluatingMainsResult.totalMarks || 15;

    if (isNaN(marksNum) || marksNum < 0 || marksNum > maxMarks) {
      alert(`Please enter valid marks between 0 and ${maxMarks}.`);
      return;
    }

    if (evaluateMainsSubmission) {
      evaluateMainsSubmission(evaluatingMainsResult.id, {
        marksObtained: marksNum,
        feedback: mainsEvalForm.feedback,
        evaluationStatus: mainsEvalForm.evaluationStatus,
      });
    } else if (updateStudentResult) {
      updateStudentResult(evaluatingMainsResult.id, {
        marksObtained: marksNum,
        percentage: Math.round((marksNum / maxMarks) * 100),
        feedback: mainsEvalForm.feedback,
        evaluatorFeedback: mainsEvalForm.feedback,
        resultStatus: mainsEvalForm.evaluationStatus,
        status: mainsEvalForm.evaluationStatus,
        evaluationDate: new Date().toISOString().split("T")[0],
      });
    }

    setEvaluatingMainsResult(null);
  };

  // --------------------------------------------------------------------------
  // CSV Export for current view
  // --------------------------------------------------------------------------
  const handleExportCsv = () => {
    if (activeTab === "prelims") {
      const headers = [
        "Student Name",
        "Reg ID",
        "Email",
        "Test Name",
        "Total Questions",
        "Correct",
        "Incorrect",
        "Unanswered",
        "Marks",
        "Total Marks",
        "Percentage",
        "Date",
      ];
      const rows = filteredPrelimsResults.map((r) => [
        `"${r.studentName}"`,
        `"${r.regId}"`,
        `"${r.email || ""}"`,
        `"${r.initiativeName}"`,
        r.totalQuestions || 10,
        r.correctAnswers ?? 8,
        r.incorrectAnswers ?? 2,
        r.unanswered ?? 0,
        r.marksObtained,
        r.totalMarks,
        `${r.percentage}%`,
        `"${r.submissionDate}"`,
      ]);
      const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
      downloadCsv(csv, "Prelims_Student_Results");
    } else {
      const headers = [
        "Student Name",
        "Reg ID",
        "Email",
        "Initiative Name",
        "GS Paper",
        "Marks Obtained",
        "Max Marks",
        "Status",
        "Date",
      ];
      const rows = filteredMainsResults.map((r) => [
        `"${r.studentName}"`,
        `"${r.regId}"`,
        `"${r.email || ""}"`,
        `"${r.initiativeName}"`,
        `"${r.gsPaper || ""}"`,
        r.marksObtained !== null ? r.marksObtained : "Pending",
        r.totalMarks,
        `"${r.resultStatus || r.status || "Pending"}"`,
        `"${r.submissionDate}"`,
      ]);
      const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
      downloadCsv(csv, "Mains_Student_Results");
    }
  };

  const downloadCsv = (content, filename) => {
    const encoded = encodeURI("data:text/csv;charset=utf-8," + content);
    const link = document.createElement("a");
    link.setAttribute("href", encoded);
    link.setAttribute("download", `${filename}_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2.5">
            <GraduationCap className="w-6 h-6 text-red-900" />
            Student Results
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Track student test performance, review scores, and evaluate Mains answer submissions.
          </p>
        </div>

        {/* Centered Tabs (Exact match with Question Bank reference) */}
        <div className="inline-flex p-1 bg-white border border-slate-200/90 rounded-2xl shadow-xs self-start sm:self-auto">
          <button
            onClick={() => setActiveTab("prelims")}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold transition-all ${
              activeTab === "prelims"
                ? "bg-red-950 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <CheckSquare size={14} />
            <span>Prelims Results</span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                activeTab === "prelims"
                  ? "bg-red-800 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {allPrelimsResults.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab("mains")}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold transition-all ${
              activeTab === "mains"
                ? "bg-red-950 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <PenTool size={14} />
            <span>Mains Results</span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                activeTab === "mains"
                  ? "bg-red-800 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {allMainsResults.length}
            </span>
          </button>
        </div>
      </div>

      {/* ==================================================================== */}
      {/* SECTION 1: PRELIMS RESULTS TAB                                       */}
      {/* ==================================================================== */}
      {activeTab === "prelims" && (
        <div className="space-y-4">
          {/* Filters Bar */}
          <div className="bg-white p-3.5 rounded-2xl border border-slate-200/90 shadow-xs space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Search Student */}
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={prelimsSearch}
                  onChange={(e) => setPrelimsSearch(e.target.value)}
                  placeholder="Search student, email, Reg ID..."
                  className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
                />
              </div>

              {/* Test Name Filter */}
              <div>
                <select
                  value={prelimsTestFilter}
                  onChange={(e) => setPrelimsTestFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-900"
                >
                  <option value="All">All Tests</option>
                  {uniquePrelimsTests.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject Filter */}
              <div>
                <select
                  value={prelimsSubjectFilter}
                  onChange={(e) => setPrelimsSubjectFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-900"
                >
                  <option value="All">All Subjects</option>
                  <option value="Polity">Polity</option>
                  <option value="History">History</option>
                  <option value="Economy">Economy</option>
                  <option value="Environment">Environment</option>
                  <option value="Science">Science &amp; Tech</option>
                  <option value="Current Affairs">Current Affairs</option>
                </select>
              </div>

              {/* Date Filter & Export */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    type="date"
                    value={prelimsDateFilter}
                    onChange={(e) => setPrelimsDateFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-900"
                  />
                </div>
                <button
                  onClick={handleExportCsv}
                  title="Export results to CSV"
                  className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex-shrink-0"
                >
                  <Download size={15} />
                </button>
              </div>
            </div>

            {(prelimsSearch || prelimsTestFilter !== "All" || prelimsSubjectFilter !== "All" || prelimsDateFilter) && (
              <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
                <span className="text-slate-500">
                  Showing <strong>{filteredPrelimsResults.length}</strong> of {allPrelimsResults.length} results
                </span>
                <button
                  onClick={() => {
                    setPrelimsSearch("");
                    setPrelimsTestFilter("All");
                    setPrelimsSubjectFilter("All");
                    setPrelimsDateFilter("");
                  }}
                  className="text-red-900 font-bold hover:underline flex items-center gap-1"
                >
                  <RotateCcw size={11} /> Reset Filters
                </button>
              </div>
            )}
          </div>

          {/* Prelims Results Table */}
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                    <th className="py-3 px-4">Student Name</th>
                    <th className="py-3 px-3">Email</th>
                    <th className="py-3 px-4">Test Name</th>
                    <th className="py-3 px-2.5 text-center">Questions</th>
                    <th className="py-3 px-2.5 text-center">Correct</th>
                    <th className="py-3 px-2.5 text-center">Incorrect</th>
                    <th className="py-3 px-2.5 text-center">Unanswered</th>
                    <th className="py-3 px-3 text-center">Marks</th>
                    <th className="py-3 px-3 text-center">Percentage</th>
                    <th className="py-3 px-3 text-center">Submission Date</th>
                    <th className="py-3 px-4 text-right">View Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {filteredPrelimsResults.map((r) => {
                    const totalQ = r.totalQuestions || 10;
                    const correct = r.correctAnswers ?? 8;
                    const incorrect = r.incorrectAnswers ?? 2;
                    const unanswered = r.unanswered ?? 0;
                    const pct = r.percentage || Math.round(((r.marksObtained || 0) / (r.totalMarks || 20)) * 100);

                    return (
                      <tr key={r.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-red-900 text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
                              {r.studentName ? r.studentName.charAt(0) : "S"}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-slate-900 truncate">
                                {r.studentName}
                              </p>
                              <span className="text-[10px] text-slate-400 font-mono block">
                                {r.regId || "SIA-2026"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3 font-mono text-[11px] text-slate-600">
                          {r.email || "aspirant@gmail.com"}
                        </td>
                        <td className="py-3 px-4 max-w-xs">
                          <p className="font-semibold text-slate-800 line-clamp-1">
                            {r.initiativeName}
                          </p>
                        </td>
                        <td className="py-3 px-2.5 text-center font-bold text-slate-700">
                          {totalQ}
                        </td>
                        <td className="py-3 px-2.5 text-center">
                          <span className="inline-block px-2 py-0.5 rounded-md font-bold text-emerald-800 bg-emerald-50 text-[11px]">
                            {correct}
                          </span>
                        </td>
                        <td className="py-3 px-2.5 text-center">
                          <span className="inline-block px-2 py-0.5 rounded-md font-bold text-rose-800 bg-rose-50 text-[11px]">
                            {incorrect}
                          </span>
                        </td>
                        <td className="py-3 px-2.5 text-center font-bold text-slate-400">
                          {unanswered}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className="font-extrabold text-slate-900">
                            {r.marksObtained !== undefined ? r.marksObtained : "—"}
                          </span>
                          <span className="text-[10px] text-slate-400 block font-mono">
                            / {r.totalMarks || 20}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full font-bold text-[11px] ${
                              pct >= 75
                                ? "bg-emerald-100 text-emerald-800"
                                : pct >= 50
                                ? "bg-amber-100 text-amber-800"
                                : "bg-rose-100 text-rose-800"
                            }`}
                          >
                            {pct}%
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center font-mono text-[11px] text-slate-500 whitespace-nowrap">
                          {r.submissionDate || "—"}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => setViewingPrelimsResult(r)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-900 font-bold rounded-lg transition-colors shadow-2xs"
                          >
                            <Eye size={13} /> View Result
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {filteredPrelimsResults.length === 0 && (
                    <tr>
                      <td colSpan={11} className="py-12 text-center text-slate-400">
                        <GraduationCap className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                        <p className="text-xs font-semibold text-slate-700">
                          No Prelims results match the selected filters
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* SECTION 2: MAINS RESULTS TAB                                         */}
      {/* ==================================================================== */}
      {activeTab === "mains" && (
        <div className="space-y-4">
          {/* Filters Bar */}
          <div className="bg-white p-3.5 rounded-2xl border border-slate-200/90 shadow-xs space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {/* Search Student */}
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={mainsSearch}
                  onChange={(e) => setMainsSearch(e.target.value)}
                  placeholder="Search student, email, Reg ID..."
                  className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
                />
              </div>

              {/* GS Paper */}
              <div>
                <select
                  value={mainsPaperFilter}
                  onChange={(e) => setMainsPaperFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-900"
                >
                  <option value="All">All GS Papers</option>
                  <option value="GS Paper 1">GS Paper I</option>
                  <option value="GS Paper 2">GS Paper II</option>
                  <option value="GS Paper 3">GS Paper III</option>
                  <option value="GS Paper 4">GS Paper IV</option>
                </select>
              </div>

              {/* Initiative Name */}
              <div>
                <select
                  value={mainsInitiativeFilter}
                  onChange={(e) => setMainsInitiativeFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-900"
                >
                  <option value="All">All Mains Initiatives</option>
                  {uniqueMainsInitiatives.map((init) => (
                    <option key={init} value={init}>
                      {init}
                    </option>
                  ))}
                </select>
              </div>

              {/* Evaluation Status */}
              <div>
                <select
                  value={mainsStatusFilter}
                  onChange={(e) => setMainsStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-900"
                >
                  <option value="All">All Evaluation Statuses</option>
                  <option value="Evaluated">Evaluated</option>
                  <option value="Pending">Pending Evaluation</option>
                </select>
              </div>

              {/* Date & Export */}
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={mainsDateFilter}
                  onChange={(e) => setMainsDateFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-900"
                />
                <button
                  onClick={handleExportCsv}
                  title="Export results to CSV"
                  className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex-shrink-0"
                >
                  <Download size={15} />
                </button>
              </div>
            </div>

            {(mainsSearch || mainsPaperFilter !== "All" || mainsInitiativeFilter !== "All" || mainsStatusFilter !== "All" || mainsDateFilter) && (
              <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
                <span className="text-slate-500">
                  Showing <strong>{filteredMainsResults.length}</strong> of {allMainsResults.length} submissions
                </span>
                <button
                  onClick={() => {
                    setMainsSearch("");
                    setMainsPaperFilter("All");
                    setMainsInitiativeFilter("All");
                    setMainsStatusFilter("All");
                    setMainsDateFilter("");
                  }}
                  className="text-red-900 font-bold hover:underline flex items-center gap-1"
                >
                  <RotateCcw size={11} /> Reset Filters
                </button>
              </div>
            )}
          </div>

          {/* Mains Results Table */}
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                    <th className="py-3 px-4">Student Name</th>
                    <th className="py-3 px-3">Email</th>
                    <th className="py-3 px-4">Initiative Name</th>
                    <th className="py-3 px-3 text-center">GS Paper</th>
                    <th className="py-3 px-4">Question Prompt</th>
                    <th className="py-3 px-3 text-center">Marks</th>
                    <th className="py-3 px-3 text-center">Max Marks</th>
                    <th className="py-3 px-3 text-center">Status</th>
                    <th className="py-3 px-3 text-center">Submission Date</th>
                    <th className="py-3 px-4 text-right">View Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {filteredMainsResults.map((r) => {
                    const isEvaluated =
                      (r.resultStatus || r.status || "").toLowerCase().includes("evaluated") &&
                      r.marksObtained !== null;

                    return (
                      <tr key={r.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-red-900 text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
                              {r.studentName ? r.studentName.charAt(0) : "S"}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-slate-900 truncate">
                                {r.studentName}
                              </p>
                              <span className="text-[10px] text-slate-400 font-mono block">
                                {r.regId || "SIA-2026"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3 font-mono text-[11px] text-slate-600">
                          {r.email || "aspirant@gmail.com"}
                        </td>
                        <td className="py-3 px-4 max-w-xs">
                          <p className="font-semibold text-slate-800 line-clamp-1">
                            {r.initiativeName}
                          </p>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className="inline-block px-2 py-0.5 rounded-md font-bold text-[10px] bg-red-100 text-red-900">
                            {r.gsPaper || "GS Paper"}
                          </span>
                        </td>
                        <td className="py-3 px-4 max-w-xs">
                          <p className="font-serif text-slate-600 line-clamp-1">
                            {r.studentAnswerText
                              ? r.studentAnswerText.substring(0, 60) + "..."
                              : r.initiativeName}
                          </p>
                        </td>
                        <td className="py-3 px-3 text-center">
                          {isEvaluated ? (
                            <span className="font-extrabold text-slate-900">
                              {r.marksObtained}
                            </span>
                          ) : (
                            <span className="text-[11px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center font-bold text-slate-600">
                          {r.totalMarks || 15} M
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-full font-bold text-[10px] ${
                              isEvaluated
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-amber-100 text-amber-800"
                            }`}
                          >
                            {isEvaluated ? "Evaluated" : "Pending Review"}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center font-mono text-[11px] text-slate-500 whitespace-nowrap">
                          {r.submissionDate || "—"}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => openMainsEvalModal(r)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-900 font-bold rounded-lg transition-colors shadow-2xs"
                          >
                            <Eye size={13} /> {isEvaluated ? "View Evaluation" : "Evaluate"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {filteredMainsResults.length === 0 && (
                    <tr>
                      <td colSpan={10} className="py-12 text-center text-slate-400">
                        <PenTool className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                        <p className="text-xs font-semibold text-slate-700">
                          No Mains submissions match the selected filters
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* MODAL 1: PRELIMS DETAILED RESULT VIEW                                */}
      {/* ==================================================================== */}
      {viewingPrelimsResult && (
        <Modal
          isOpen={!!viewingPrelimsResult}
          onClose={() => setViewingPrelimsResult(null)}
          title="Prelims Test Performance Summary"
          subtitle={`${viewingPrelimsResult.initiativeName} • ${viewingPrelimsResult.studentName}`}
          maxWidth="max-w-3xl"
        >
          <div className="space-y-4">
            {/* Student & Score Card Header */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-red-900 text-white font-bold text-sm flex items-center justify-center flex-shrink-0 shadow-xs">
                  {viewingPrelimsResult.studentName ? viewingPrelimsResult.studentName.charAt(0) : "S"}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    {viewingPrelimsResult.studentName}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-0.5 text-[11px] text-slate-500">
                    <span className="font-mono">{viewingPrelimsResult.regId}</span>
                    <span>•</span>
                    <span>{viewingPrelimsResult.email || "aspirant@gmail.com"}</span>
                    <span>•</span>
                    <span>{viewingPrelimsResult.submissionDate}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-xl border border-slate-200 shadow-2xs">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                    Marks Scored
                  </span>
                  <span className="text-base font-black text-slate-900">
                    {viewingPrelimsResult.marksObtained}
                    <span className="text-xs text-slate-400 font-normal">
                      {" "}/ {viewingPrelimsResult.totalMarks || 20}
                    </span>
                  </span>
                </div>
                <div className="border-l border-slate-200 pl-3">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                    Accuracy
                  </span>
                  <span className="text-base font-black text-emerald-700">
                    {viewingPrelimsResult.percentage}%
                  </span>
                </div>
              </div>
            </div>

            {/* Score Metrics 4-Box Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 text-center">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  Total Questions
                </span>
                <p className="text-lg font-black text-slate-900 mt-0.5">
                  {viewingPrelimsResult.totalQuestions || 10}
                </p>
              </div>

              <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-200/80 text-center">
                <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">
                  Correct (+2.00)
                </span>
                <p className="text-lg font-black text-emerald-700 mt-0.5">
                  {viewingPrelimsResult.correctAnswers ?? 8}
                </p>
              </div>

              <div className="p-3 bg-rose-50/60 rounded-xl border border-rose-200/80 text-center">
                <span className="text-[10px] font-bold text-rose-800 uppercase tracking-wider">
                  Incorrect (-0.66)
                </span>
                <p className="text-lg font-black text-rose-700 mt-0.5">
                  {viewingPrelimsResult.incorrectAnswers ?? 2}
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 text-center">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  Unanswered (0.00)
                </span>
                <p className="text-lg font-black text-slate-600 mt-0.5">
                  {viewingPrelimsResult.unanswered ?? 0}
                </p>
              </div>
            </div>

            {/* Question-wise Answer Breakdown */}
            <div className="space-y-2 pt-2">
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center justify-between">
                <span>Question-Wise Performance Breakdown</span>
                <span className="text-[11px] text-slate-500 normal-case font-normal">
                  Marking scheme: +2.00 for correct, -0.66 for incorrect
                </span>
              </h4>

              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-xl pr-1">
                {(viewingPrelimsResult.details || [
                  { qNum: 1, topic: "Polity", isCorrect: true, userPick: "Option A", correctPick: "Option A" },
                  { qNum: 2, topic: "Science & Tech", isCorrect: true, userPick: "Option C", correctPick: "Option C" },
                  { qNum: 3, topic: "Environment", isCorrect: false, userPick: "Option B", correctPick: "Option A" },
                  { qNum: 4, topic: "Economy", isCorrect: true, userPick: "Option B", correctPick: "Option B" },
                  { qNum: 5, topic: "Geography", isCorrect: true, userPick: "Option D", correctPick: "Option D" },
                ]).map((item, idx) => (
                  <div
                    key={idx}
                    className={`p-3 text-xs flex items-start gap-3 transition-colors ${
                      item.isCorrect ? "hover:bg-slate-50" : "bg-rose-50/30"
                    }`}
                  >
                    <div className="w-6 h-6 rounded-md font-mono font-bold text-xs flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-700">
                      Q{item.qNum || idx + 1}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">
                          {item.topic || "General Studies"}
                        </span>
                        {item.isCorrect ? (
                          <span className="text-[10px] font-bold text-emerald-700 flex items-center gap-1">
                            <CheckCircle2 size={12} /> Correct (+2.00 M)
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-rose-700 flex items-center gap-1">
                            <XCircle size={12} /> Incorrect (-0.66 M)
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1 text-[11px]">
                        <p className="text-slate-600">
                          Student Selected:{" "}
                          <strong className={item.isCorrect ? "text-emerald-800" : "text-rose-700"}>
                            {item.userPick}
                          </strong>
                        </p>
                        <p className="text-slate-600">
                          Correct Option:{" "}
                          <strong className="text-emerald-800 font-bold">
                            &lt;CO&gt; {item.correctPick}
                          </strong>
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ==================================================================== */}
      {/* MODAL 2: MAINS SUBMISSION EVALUATION & DETAILED VIEW                 */}
      {/* ==================================================================== */}
      {evaluatingMainsResult && (
        <Modal
          isOpen={!!evaluatingMainsResult}
          onClose={() => setEvaluatingMainsResult(null)}
          title="Mains Answer Evaluation & Performance"
          subtitle={`${evaluatingMainsResult.initiativeName} • ${evaluatingMainsResult.studentName}`}
          maxWidth="max-w-3xl"
        >
          <form onSubmit={handleSaveMainsEvaluation} className="space-y-4">
            {/* Student & Meta Details */}
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/90 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-red-900 text-white font-bold flex items-center justify-center flex-shrink-0">
                  {evaluatingMainsResult.studentName ? evaluatingMainsResult.studentName.charAt(0) : "S"}
                </div>
                <div>
                  <p className="font-bold text-slate-900">
                    {evaluatingMainsResult.studentName}
                  </p>
                  <p className="text-[10px] text-slate-500 font-mono">
                    {evaluatingMainsResult.regId} • {evaluatingMainsResult.email || "aspirant@gmail.com"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-900">
                  {evaluatingMainsResult.gsPaper || "GS Paper"}
                </span>
                <span className="text-[11px] text-slate-500 font-medium flex items-center gap-1">
                  <Clock size={12} /> {evaluatingMainsResult.submissionDate}
                </span>
                <span className="text-[11px] font-mono text-slate-600 font-bold bg-white px-2 py-0.5 rounded border border-slate-200">
                  {evaluatingMainsResult.wordCount || 230} words
                </span>
              </div>
            </div>

            {/* Mains Question Prompt */}
            <div className="p-3.5 bg-red-50/30 rounded-xl border border-red-100">
              <span className="text-[10px] font-bold text-red-900 uppercase tracking-wider block mb-1">
                Mains Question Prompt
              </span>
              <p className="text-xs font-serif font-medium text-slate-900 leading-relaxed">
                {evaluatingMainsResult.initiativeName}: Evaluate the core structural and operational challenges, citing relevant committees and constitutional precedents. (Max {evaluatingMainsResult.totalMarks || 15} Marks)
              </p>
            </div>

            {/* Full Submitted Answer */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Student's Submitted Answer
                </label>
                <span className="text-[11px] text-slate-400">
                  {evaluatingMainsResult.wordCount || 240} Words Submitted
                </span>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs font-serif leading-relaxed text-slate-800 max-h-56 overflow-y-auto whitespace-pre-line shadow-inner">
                {evaluatingMainsResult.studentAnswerText ||
                  `The issue under scrutiny holds paramount significance in contemporary Indian governance and constitutional democracy.

Constitutional Framework & Principles:
Under the constitutional scheme, institutional checks and balances safeguard public accountability and fundamental liberties. As observed in landmark Supreme Court precedents, administrative mechanisms must balance executive efficiency with rigorous legislative oversight.

Key Structural Challenges:
1. Operational Bottlenecks: Declining average session hours and referral rates of critical policy bills.
2. Resource Asymmetries: Inadequate research backstopping and institutional staffing across administrative wings.
3. Federal Consultation: Limited pre-legislative stakeholder consultation leading to post-facto legal friction.

Way Forward & Recommendations:
1. Institutionalize mandatory scrutiny rules aligned with global best practices.
2. Establish autonomous legislative research wings for objective impact assessments.
3. Enforce strict action-taken report (ATR) timelines to ensure executive responsiveness.`}
              </div>
            </div>

            {/* Evaluation Form Controls */}
            <div className="p-4 bg-slate-50/80 rounded-2xl border border-slate-200 space-y-3">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center justify-between">
                <span>Evaluator Marks &amp; Feedback</span>
                <span className="text-[10px] font-bold text-red-900 bg-red-100/70 px-2 py-0.5 rounded-full">
                  Admin Evaluator Portal
                </span>
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Marks Input */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Marks Awarded (Max {evaluatingMainsResult.totalMarks || 15}) *
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max={evaluatingMainsResult.totalMarks || 15}
                    required
                    value={mainsEvalForm.marksObtained}
                    onChange={(e) =>
                      setMainsEvalForm({ ...mainsEvalForm, marksObtained: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-black text-slate-900 focus:outline-none focus:ring-1 focus:ring-red-900"
                  />
                </div>

                {/* Status Toggle */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Evaluation Status *
                  </label>
                  <select
                    value={mainsEvalForm.evaluationStatus}
                    onChange={(e) =>
                      setMainsEvalForm({
                        ...mainsEvalForm,
                        evaluationStatus: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
                  >
                    <option value="Evaluated">Evaluated &amp; Published</option>
                    <option value="Pending Evaluation">Pending Evaluation</option>
                  </select>
                </div>

                {/* Evaluator Name */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Evaluator Sign-off
                  </label>
                  <input
                    type="text"
                    value={mainsEvalForm.evaluatorName}
                    onChange={(e) =>
                      setMainsEvalForm({ ...mainsEvalForm, evaluatorName: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700"
                  />
                </div>
              </div>

              {/* Feedback Textarea */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Constructive Evaluator Feedback *
                </label>
                <textarea
                  rows={3}
                  required
                  value={mainsEvalForm.feedback}
                  onChange={(e) =>
                    setMainsEvalForm({ ...mainsEvalForm, feedback: e.target.value })
                  }
                  placeholder="Provide structured feedback: evaluate introduction, body arguments, use of constitutional articles / examples, and conclusion..."
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setEvaluatingMainsResult(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-bold text-white bg-red-900 hover:bg-red-800 rounded-xl shadow-xs"
              >
                <Check size={14} /> Save &amp; Publish Evaluation
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default StudentResults;
